// src/pages/Anaco/AdvancedToolsIntegration.js
// RENAMED: Page showcasing the VISION of an integrated Marketplace with <InheritedStyleLink to="/anaco/terraplan">TerraPlan™</InheritedStyleLink>
// UPDATED: Hover animation loops continuously (carousel).
// Uses Green/White/Grey Theme, Spanish Language. Uses aspirational language.
// Current time: Friday, May 2, 2025 at 6:21 PM CST. San José, Costa Rica.

import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import {
  FiMap,
  FiLayers,
  FiBarChart2,
  FiTool,
  FiCheckCircle,
  FiLayout,
  FiEye,
  FiFastForward,
  FiShield,
  FiInfo,
  FiArrowRight,
  FiPhone,
  FiHome,
  FiBriefcase,
  FiPackage,
  FiDroplet,
  FiActivity,
  FiWind,
  FiDatabase,
  FiZap,
  FiMessageSquare,
  FiCpu,
  FiLoader,
  FiShare2,
} from "react-icons/fi";

// Reusable Components (Ideally import Button, Section, BenefitItem from separate files)
import Button from "../../components/Button";
import InheritedStyleLink from "../../components/InheritedStyleLink";

// Theme Colors (Ideally import from a central config/context)
const colors = {
  background: "bg-slate-100",
  surface: "bg-white",
  primary: "text-emerald-700",
  primaryBg: "bg-emerald-600",
  primaryBgHover: "hover:bg-emerald-700",
  secondary: "text-slate-600",
  textDark: "text-slate-800",
  textLight: "text-white",
  border: "border-slate-300",
  borderLight: "border-slate-200",
  primaryLightBg: "bg-emerald-50",
  accentSuccess: "text-emerald-500",
  warningBg: "bg-yellow-50",
  warningBorder: "border-yellow-300",
  warningIcon: "text-yellow-500",
};

// Animation Variants (Ideally import from a central config)
const viewportOnce = { once: true, amount: 0.1 };
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};
const staggerContainer = {
  visible: { transition: { staggerChildren: 0.1 } },
};
const scaleUp = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: [0.33, 1, 0.68, 1] },
  },
};
const cycleTextVariant = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.2, ease: "easeIn" } },
};

// --- Reusable Section Component (Recommend moving to src/components/Section.js) ---
const Section = ({
  children,
  className = "",
  bg = colors.background,
  ariaLabelledby,
}) => (
  <motion.section
    className={`py-12 md:py-16 ${bg} ${className}`}
    initial="hidden"
    whileInView="visible"
    viewport={viewportOnce}
    variants={staggerContainer}
    aria-labelledby={ariaLabelledby}
  >
    <div className="container mx-auto px-4 sm:px-6 max-w-5xl">{children}</div>
  </motion.section>
);
Section.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  bg: PropTypes.string,
  ariaLabelledby: PropTypes.string,
};

// --- Reusable Benefit Item Component (Recommend moving to src/components/Anaco/BenefitItem.js or similar) ---
const BenefitItem = ({ icon: Icon = FiCheckCircle, children }) => (
  <li className="flex items-start space-x-3">
    <Icon
      className={`w-6 h-6 ${colors.accentSuccess} mt-0.5 flex-shrink-0`}
      aria-hidden="true"
    />
    <span className={`${colors.secondary} text-base md:text-lg`}>
      {children}
    </span>
  </li>
);
BenefitItem.propTypes = {
  icon: PropTypes.elementType,
  children: PropTypes.node.isRequired,
};

// --- Main Component ---
export default function AdvancedToolsIntegration() {
  const currentTime = new Date().toLocaleString("es-CR", {
    timeZone: "America/Costa_Rica",
    dateStyle: "medium",
    timeStyle: "short",
  });

  const developmentTypes = [
    { name: "Residencial", icon: FiHome },
    { name: "Comercial", icon: FiBriefcase },
    { name: "Industrial", icon: FiPackage },
    { name: "Turístico", icon: FiWind },
    { name: "Agrícola", icon: FiDroplet },
    { name: "Salud", icon: FiActivity },
    { name: "Uso Mixto", icon: FiLayers },
  ];

  // --- State for Hover Simulation ---
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulationMessageIndex, setSimulationMessageIndex] = useState(0);
  const simulationTimerRef = useRef(null);
  const simulationMessages = [
    "Cargando datos del terreno...",
    "Analizando topografía y acceso...",
    "Consultando normativas...",
    "Calculando potencial de desarrollo...",
    "Generando estimación de pre-aprobación...",
    "¡Propuesta preliminar lista!",
  ];
  const simulationInterval = 1500; // ms between messages

  // Effect for Looping Animation
  useEffect(() => {
    if (isSimulating) {
      simulationTimerRef.current = setInterval(() => {
        setSimulationMessageIndex(
          (prevIndex) => (prevIndex + 1) % simulationMessages.length
        );
      }, simulationInterval);
    } else {
      clearInterval(simulationTimerRef.current);
    }
    return () => clearInterval(simulationTimerRef.current);
  }, [isSimulating, simulationMessages.length]);

  // Hover Handlers
  const handleHoverStart = () => {
    if (!isSimulating) {
      // Prevent resetting index if already simulating
      setSimulationMessageIndex(0);
    }
    setIsSimulating(true);
  };

  const handleHoverEnd = () => {
    setIsSimulating(false);
    // Reset index immediately for clean start next hover
    // Note: This might cause a brief flash of the first message if re-hovered quickly,
    // depending on exact timing, but ensures it always starts fresh.
    setSimulationMessageIndex(0);
  };

  return (
    <div className={`${colors.background} ${colors.textDark} font-sans`}>
      {/* Assumes AnacoLayout provides Header/Footer */}
      <main>
        {/* Hero Section */}
        <Section
          bg={colors.surface}
          className={`border-b ${colors.borderLight} pt-20 md:pt-28 pb-16 md:pb-20`}
          ariaLabelledby="vision-hero-title"
        >
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div
              variants={scaleUp}
              className={`inline-block p-4 mb-5 rounded-full ${colors.accentBlueBg} border ${colors.border}`}
            >
              <FiZap className={`w-12 h-12 ${colors.accentSuccess}`} />
            </motion.div>
            <motion.h1
              variants={fadeInUp}
              id="vision-hero-title"
              className={`text-4xl md:text-5xl font-bold ${colors.textDark} mb-5`}
            >
              Nuestra Visión: Integración Avanzada de Herramientas
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className={`text-lg md:text-xl ${colors.secondary} max-w-3xl mx-auto`}
            >
              Imagine un ecosistema donde el análisis de propiedades con {""}
              <InheritedStyleLink to="/anaco/terraplan">TerraPlan™</InheritedStyleLink> se conecta directamente con la pre-aprobación
              hipotecaria dentro de ANACO Conecta, agilizando todo el proceso.
            </motion.p>
          </motion.div>
        </Section>

        {/* Conceptual Workflow Section */}
        <Section ariaLabelledby="workflow-title">
          <motion.h2
            variants={fadeInUp}
            id="workflow-title"
            className={`text-3xl font-bold ${colors.textDark} text-center mb-12 md:mb-16`}
          >
            Visualizando el Futuro Flujo de Trabajo
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
            {/* Step 1: Find Property */}
            <motion.div
              variants={fadeInUp}
              className={`p-6 rounded-lg ${colors.surface} border ${colors.borderLight} shadow-md text-center flex flex-col h-full`}
            >
              <FiDatabase
                className={`w-10 h-10 ${colors.primary} mx-auto mb-3`}
              />
              <h3 className={`text-lg font-semibold ${colors.textDark} mb-2`}>
                1. Explore Propiedades
              </h3>
              <p className={`text-sm ${colors.secondary} mb-4 flex-grow`}>
                Navegue un posible mercado de listados verificados dentro de
                ANACO Conecta.
              </p>
              {/* Placeholder 1 */}
              <div className="aspect-[4/3] mt-auto rounded overflow-hidden shadow-inner">
                <img
                  src="/images/residential.jpeg"
                  alt="roof view of residential homes"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </motion.div>

            {/* Step 2: Run <InheritedStyleLink to="/anaco/terraplan">TerraPlan™</InheritedStyleLink> */}
            <motion.div
              variants={fadeInUp}
              className={`p-6 rounded-lg ${colors.surface} border ${colors.borderLight} shadow-md text-center flex flex-col h-full`}
            >
              <FiMap className={`w-10 h-10 ${colors.primary} mx-auto mb-3`} />
              <h3 className={`text-lg font-semibold ${colors.textDark} mb-2`}>
                2. Analice con <InheritedStyleLink to="/anaco/terraplan">TerraPlan™</InheritedStyleLink>
              </h3>
              <p className={`text-sm ${colors.secondary} mb-4 flex-grow`}>
                Ejecute análisis de terreno y potencial de desarrollo
                directamente en el listado.
              </p>
              {/* Image */}
              <div className="aspect-[4/3] mt-auto rounded overflow-hidden shadow-inner">
                <img
                  src="/images/air-view-sonar.webp"
                  alt="visualización del análisis TerraPlan sobre vista aérea"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </motion.div>

            {/* Step 3: Automated Insight/Pre-Approval (Hover Animation) */}
            <motion.div
              variants={fadeInUp}
              className={`p-6 rounded-lg ${colors.surface} border ${colors.borderLight} shadow-md text-center flex flex-col h-full`}
            >
              <FiCheckCircle
                className={`w-10 h-10 ${colors.accentSuccess} mx-auto mb-3`}
              />
              <h3 className={`text-lg font-semibold ${colors.textDark} mb-2`}>
                3. Pre-Aprobación Inteligente*
              </h3>
              <p className={`text-sm ${colors.secondary} mb-4 flex-grow`}>
                Combine el análisis <InheritedStyleLink to="/anaco/terraplan">TerraPlan™</InheritedStyleLink> con su perfil financiero para
                obtener una estimación de pre-aprobación instantánea para esa
                propiedad específica.
              </p>

              {/* --- Hover Simulation Area --- */}
              <motion.div
                className="aspect-[4/3] mt-auto bg-emerald-50 border border-emerald-200 rounded flex flex-col items-center justify-center text-emerald-600 text-xs p-4 overflow-hidden relative cursor-pointer"
                onHoverStart={handleHoverStart}
                onHoverEnd={handleHoverEnd}
                title="Pase el ratón para simular análisis"
              >
                <AnimatePresence mode="wait">
                  {isSimulating ? (
                    <motion.div
                      key={simulationMessageIndex} // Key triggers transition
                      className="flex flex-col items-center justify-center text-center absolute inset-0 bg-emerald-50/95 backdrop-blur-sm p-2"
                      variants={cycleTextVariant}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      <FiLoader className="w-5 h-5 animate-spin mb-2" />
                      <p className="font-medium text-sm leading-tight">
                        {simulationMessages[simulationMessageIndex]}
                      </p>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="default"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="text-center text-emerald-500"
                    >
                      <FiCheckCircle className="w-10 h-10 mx-auto mb-1 opacity-50" />
                      <span className="font-medium text-xs block">
                        Simular Análisis
                      </span>
                      <span className="font-medium text-xs block">
                        (Pasar Ratón)
                      </span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
              {/* --- END Hover Simulation Area --- */}
            </motion.div>
            {/* End Step 3 */}
          </div>
          <motion.p
            variants={fadeInUp}
            className={`text-xs text-center ${colors.secondary} mt-6`}
          >
            *La pre-aprobación inteligente es conceptual y dependería de la
            integración de datos financieros y análisis de riesgo adicionales.
          </motion.p>
        </Section>

        {/* Potential Integration / Custom UI Section */}
        <Section
          bg={colors.surface}
          className={`border-y ${colors.borderLight}`}
          ariaLabelledby="integration-title"
        >
          {/* ... Integration section content ... */}
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <motion.div variants={fadeInUp}>
              <h2
                id="integration-title"
                className={`text-3xl font-bold ${colors.textDark} mb-4`}
              >
                Potencial de Integración Futura
              </h2>
              <p className={`${colors.secondary} text-lg leading-relaxed mb-6`}>
                Nuestra visión incluye extender estas capacidades más allá de
                ANACO Conecta. Exploramos posibilidades como:
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <FiLayout className={`w-7 h-7 mr-4 mt-1 ${colors.primary}`} />
                  <span className={colors.textDark}>
                    <strong>APIs para Socios Clave:</strong> Permitir la
                    integración de análisis y datos de estado en las
                    herramientas propias de agentes o desarrolladores
                    seleccionados.
                  </span>
                </li>
                <li className="flex items-start">
                  <FiTool className={`w-7 h-7 mr-4 mt-1 ${colors.primary}`} />
                  <span className={colors.textDark}>
                    <strong>Componentes Incrustables:</strong> Ofrecer widgets
                    seguros (calculadora, visualizador de planes) para sitios
                    web de socios estratégicos.
                  </span>
                </li>
              </ul>
              <p className={`${colors.secondary} text-base mt-5`}>
                Estas integraciones crearían un ecosistema inmobiliario y
                financiero más conectado y eficiente en Costa Rica.
              </p>
            </motion.div>
            <motion.div variants={fadeInUp}>
              {/* Image Placeholder 4 */}
              <div className="aspect-[4/3] mt-auto rounded overflow-hidden shadow-inner">
                <img
                  src="/images/business.jpeg"
                  alt="visualización del análisis TerraPlan sobre vista aérea"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </motion.div>
          </div>
        </Section>

        {/* Call to Action for Vision */}
        <Section ariaLabelledby="vision-cta-title">
          {/* ... CTA Section Content ... */}
          <motion.div
            variants={fadeInUp}
            className={`text-center max-w-3xl mx-auto p-10 rounded-lg ${colors.primaryLightBg} border ${colors.border} shadow-lg`}
          >
            <FiCpu className={`w-12 h-12 ${colors.primary} mx-auto mb-4`} />
            <h2
              id="vision-cta-title"
              className={`text-3xl font-bold ${colors.textDark} mb-4`}
            >
              ¿Interesado en Co-Crear Este Futuro?
            </h2>
            <p className={`text-lg ${colors.secondary} mb-8`}>
              Esta visión integrada representa la próxima evolución de ANACO
              Conecta. Si estas capacidades avanzadas le interesan y le gustaría
              participar en futuras discusiones o programas piloto, háganoslo
              saber.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button
                to="/anaco/contact?subject=Interes%20Vision%20Herramientas%20Avanzadas"
                variant="primary"
                size="lg"
                icon={<FiMessageSquare />}
                className={`${colors.primaryBg} ${colors.primaryBgHover} ${colors.textLight}`}
              >
                Registrar Interés / Compartir Ideas
              </Button>
            </div>
          </motion.div>
        </Section>
      </main>
    </div>
  );
}

// --- PropTypes ---
// Recommend defining PropTypes for Section and BenefitItem if moved to separate files
// Section.propTypes = { /* ... */ };
// BenefitItem.propTypes = { /* ... */ };
